import React from 'react';
import Icon from '../../components/Icon';
import './LanguagePicker.css';

/**
 * LANGUAGES — the pinned starter set for the composer picker (F12.2).
 *
 * Each entry feeds `LanguagePicker` AND the highlighter's grammar map. Adding
 * a language here requires a matching `src/features/codedrop/grammars/<id>.js`
 * file or the live preview will fall back to plain spans (no error).
 */
export const LANGUAGES = [
  { id: 'ts', label: 'TypeScript', ext: 'ts' },
  { id: 'js', label: 'JavaScript', ext: 'js' },
  { id: 'py', label: 'Python', ext: 'py' },
  { id: 'bash', label: 'Bash', ext: 'sh' },
  { id: 'json', label: 'JSON', ext: 'json' },
  { id: 'md', label: 'Markdown', ext: 'md' },
  { id: 'css', label: 'CSS', ext: 'css' },
  { id: 'html', label: 'HTML', ext: 'html' },
  { id: 'go', label: 'Go', ext: 'go' },
  { id: 'rs', label: 'Rust', ext: 'rs' },
];

/**
 * LanguagePicker — `<select>` styled to match the artboard 20 chip.
 *
 * Native `<select>` keeps keyboard / mobile picker behaviour without a custom
 * popover. The visible label is rendered behind the (transparent) native
 * element so we can compose the design's `code` icon + `chevron-down` next to
 * the current language name — the artboard treats this as a chip, not a
 * standard form control.
 *
 * Props:
 *  - value      string  — current language id (e.g. 'ts').
 *  - onChange   (id) => void — fires when the user picks a different language.
 *  - languages  ?array — override the default LANGUAGES list (mostly for tests).
 */
const LanguagePicker = ({ value, onChange, languages = LANGUAGES }) => {
  const current = languages.find((l) => l.id === value) ?? languages[0];

  const handleChange = (event) => {
    const next = event.target.value;
    if (typeof onChange === 'function') onChange(next);
  };

  return (
    <label className="lang-picker">
      <Icon name="code" size={14} stroke="#8fdcff" aria-hidden />
      <span className="lang-picker__label">{current?.label ?? '—'}</span>
      <Icon
        name="chevron-down"
        size={14}
        stroke="rgba(255,255,255,.6)"
        aria-hidden
      />
      <select
        className="lang-picker__select"
        value={current?.id ?? ''}
        onChange={handleChange}
        aria-label="Language"
      >
        {languages.map((l) => (
          <option key={l.id} value={l.id}>
            {l.label}
          </option>
        ))}
      </select>
    </label>
  );
};

export default LanguagePicker;
